/**
 * @module ProgressRoot
 *
 * @remarks
 * Root component for progress bars. Creates progress context, provides it to
 * child components, bridges v-model via useProxyModel, and emits ARIA
 * progressbar attributes.
 */

<script lang="ts">
  // Components
  import { Atom } from '#v0/components/Atom'

  // Context
  import ProgressHiddenInput from './ProgressHiddenInput.vue'

  // Composables
  import { createContext } from '#v0/composables/createContext'
  import { createProgress } from '#v0/composables/createProgress'
  import { useLocale } from '#v0/composables/useLocale'
  import { useProxyModel } from '#v0/composables/useProxyModel'

  // Utilities
  import { isArray, isNullOrUndefined, useId } from '#v0/utilities'
  import { computed, mergeProps, shallowRef, toRef, useAttrs } from 'vue'

  // Types
  import type { AtomProps } from '#v0/components/Atom'
  import type { ProgressContext, ProgressTicket } from '#v0/composables/createProgress'
  import type { ShallowRef } from 'vue'

  export interface ProgressRootContext extends ProgressContext {
    readonly id: string
    readonly name?: string
    readonly labelId: string
    /** Whether a Progress.Label child is currently mounted */
    readonly hasLabel: ShallowRef<boolean>
  }

  export interface ProgressRootProps extends AtomProps {
    id?: string
    /** Accessible label when no visible Progress.Label is used */
    ariaLabel?: string
    modelValue?: number | number[]
    min?: number
    max?: number
    name?: string
    namespace?: string
  }

  export interface ProgressRootSlotProps {
    id: string
    total: number
    percent: number
    isIndeterminate: boolean
    segments: readonly ProgressTicket[]
    attrs: {
      'role': 'progressbar'
      'aria-valuenow': number | undefined
      'aria-valuemin': number
      'aria-valuemax': number
      'aria-valuetext': string | undefined
      'aria-label': string | undefined
      'aria-labelledby': string | undefined
      'aria-busy': true | undefined
      'data-state': 'determinate' | 'indeterminate'
      'data-complete': true | undefined
    }
  }

  export const [useProgressRoot, provideProgressRoot] = /* @__PURE__ */ createContext<ProgressRootContext>()
</script>

<script setup lang="ts">
  defineOptions({ name: 'ProgressRoot', inheritAttrs: false })

  const attrs = useAttrs()

  defineSlots<{
    default: (props: ProgressRootSlotProps) => any
  }>()

  defineEmits<{
    'update:model-value': [value: number | number[]]
  }>()

  const {
    as = 'div',
    renderless,
    id = useId(),
    ariaLabel,
    min = 0,
    max = 100,
    name,
    namespace = 'v0:progress:root',
  } = defineProps<ProgressRootProps>()

  const model = defineModel<number | number[]>()

  const internal = computed({
    get: () => {
      if (isNullOrUndefined(model.value)) return []
      return isArray(model.value) ? model.value : [model.value]
    },
    set: (arr: number[]) => {
      /* v8 ignore next -- defensive guard for null/undefined model with empty registry */
      if (isNullOrUndefined(model.value) && arr.length === 0) return
      /* v8 ignore next -- arr[0] is defined once a fill has registered a value */
      model.value = isArray(model.value) ? arr : (arr[0] ?? 0)
    },
  })

  const progress = createProgress({ min, max })

  useProxyModel(progress, internal, { multiple: true })

  const locale = useLocale()
  const labelId = `${id}-label`
  const hasLabel = shallowRef(false)

  const context: ProgressRootContext = {
    ...progress,
    id,
    name,
    labelId,
    hasLabel,
  }

  provideProgressRoot(namespace, context)

  const slotProps = toRef((): ProgressRootSlotProps => {
    const indeterminate = progress.isIndeterminate.value
    const total = progress.total.value
    const pct = progress.percent.value

    return {
      id,
      total,
      percent: pct,
      isIndeterminate: indeterminate,
      segments: progress.segments.value,
      attrs: {
        'role': 'progressbar',
        'aria-valuenow': indeterminate ? undefined : total,
        'aria-valuemin': min,
        'aria-valuemax': max,
        'aria-valuetext': indeterminate ? undefined : `${Math.round(pct)}%`,
        'aria-label': hasLabel.value ? undefined : (ariaLabel ?? locale.ti('Progress.label') ?? 'Loading'),
        'aria-labelledby': hasLabel.value ? labelId : undefined,
        'aria-busy': indeterminate ? true : undefined,
        'data-state': indeterminate ? 'indeterminate' : 'determinate',
        'data-complete': total >= max ? true : undefined,
      },
    }
  })
</script>

<template>
  <Atom
    v-bind="mergeProps(attrs, slotProps.attrs)"
    :as
    :renderless
  >
    <slot v-bind="slotProps" />
  </Atom>

  <ProgressHiddenInput v-if="name" />
</template>
